import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '../config/api';

const CompleteRegistration: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasNumber: false,
    hasLetter: false,
    isStrong: false
  });
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get user data from location state
  const { userData, from = '/home' } = (location.state as { userData: any, from: string }) || {};
  
  // API base URL from configuration
  const API_URL = API_CONFIG.baseURL;

  // Password strength validation
  const validatePasswordStrength = (password: string) => {
    const hasMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasLetter = /[a-zA-Zآ-ی]/.test(password); // English and Persian letters
    const isStrong = hasMinLength && hasNumber && hasLetter;
    
    return {
      hasMinLength,
      hasNumber,
      hasLetter,
      isStrong
    };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    
    // Update password strength when password changes
    if (name === 'password') {
      const strength = validatePasswordStrength(value);
      setPasswordStrength(strength);
      // Check if passwords match when password changes
      setPasswordsMatch(value === newFormData.confirmPassword || newFormData.confirmPassword === '');
    }
    
    // Check if passwords match when confirm password changes
    if (name === 'confirmPassword') {
      setPasswordsMatch(value === newFormData.password || value === '');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Validation
    if (!formData.name.trim() || !formData.lastName.trim()) {
      setError('نام و نام خانوادگی الزامی است');
      setIsLoading(false);
      return;
    }
    
    // Strong password validation
    const strength = validatePasswordStrength(formData.password);
    if (!strength.hasMinLength) {
      setError('رمز عبور باید حداقل ۸ کاراکتر باشد');
      setIsLoading(false);
      return;
    }
    
    if (!strength.hasNumber) {
      setError('رمز عبور باید حداقل یک عدد داشته باشد');
      setIsLoading(false);
      return;
    }
    
    if (!strength.hasLetter) {
      setError('رمز عبور باید حداقل یک حرف داشته باشد');
      setIsLoading(false);
      return;
    }
    
    if (!strength.isStrong) {
      setError('رمز عبور باید ترکیبی از حروف و اعداد باشد و حداقل ۸ کاراکتر داشته باشد');
      setIsLoading(false);
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('رمز عبور و تکرار آن یکسان نیستند');
      setIsLoading(false);
      return;
    }
    
    try {
      // Get the auth token from localStorage
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('خطا در احراز هویت. لطفا مجددا وارد شوید');
        navigate('/login');
        return;
      }
      
      // Send request to complete registration
      const response = await axios.put(`${API_URL}/users/complete-registration`, {
        name: formData.name.trim(),
        lastName: formData.lastName.trim(),
        password: formData.password
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Update stored user data
        const updatedUserData = {
          ...userData,
          name: formData.name.trim(),
          lastName: formData.lastName.trim()
        };
        localStorage.setItem('userData', JSON.stringify(updatedUserData));
        
        // Navigate to home or the original destination
        navigate(from);
      } else {
        setError(response.data.message || 'خطا در تکمیل ثبت نام');
      }
    } catch (err: any) {

      setError(err.response?.data?.message || 'خطا در اتصال به سرور');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white md:bg-slate-50 flex h-dvh">
      <main className="w-full">
        <div className="max-w-[480px] mx-auto select-none flex flex-col md:justify-center md:h-dvh">
          <form className="flex flex-col items-start pt-8 md:p-12 rounded-md md:border-1 border-slate-200 bg-white" onSubmit={handleSubmit}>
            <div className="px-4 md:p-0 w-full">
              <img src="/assets/img/logoRazi.png" alt="Razi Logo" width="42px" height="42px" />

              <h1 className="text-xl font-bold text-slate-900 mt-2">تکمیل ثبت نام</h1>
              <p className="text-sm text-slate-700 mt-2 mb-6">
                لطفا اطلاعات زیر را برای تکمیل ثبت نام وارد کنید.
              </p>
              
              <div className="space-y-4">
                <div className="w-full">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    نام
                  </label>
                  <input 
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="نام خود را وارد کنید"
                    className="w-full border border-slate-200 py-2 px-4 rounded-lg text-md min-h-[54px] focus:border-[#00bcd4] focus:outline-none"
                    required
                  />
                </div>
                
                <div className="w-full">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    نام خانوادگی
                  </label>
                  <input 
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="نام خانوادگی خود را وارد کنید"
                    className="w-full border border-slate-200 py-2 px-4 rounded-lg text-md min-h-[54px] focus:border-[#00bcd4] focus:outline-none"
                    required
                  />
                </div>
                
                <div className="w-full">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    رمز عبور
                  </label>
                  <input 
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="رمز عبور قوی (حداقل ۸ کاراکتر، شامل حروف و اعداد)"
                    className="w-full border border-slate-200 py-2 px-4 rounded-lg text-md min-h-[54px] focus:border-[#00bcd4] focus:outline-none"
                    required
                    minLength={8}
                  />
                  
                  {/* Password strength indicator */}
                  {formData.password && (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <div className={`w-2 h-2 rounded-full ${passwordStrength.hasMinLength ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${passwordStrength.hasMinLength ? 'text-green-600' : 'text-gray-500'}`}>
                          حداقل ۸ کاراکتر
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <div className={`w-2 h-2 rounded-full ${passwordStrength.hasLetter ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${passwordStrength.hasLetter ? 'text-green-600' : 'text-gray-500'}`}>
                          حداقل یک حرف
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <div className={`w-2 h-2 rounded-full ${passwordStrength.hasNumber ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${passwordStrength.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                          حداقل یک عدد
                        </span>
                      </div>
                      {passwordStrength.isStrong && (
                        <div className="flex items-center space-x-2 space-x-reverse mt-2">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-xs text-green-600 font-medium">
                            رمز عبور قوی ✓
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="w-full">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    تکرار رمز عبور
                  </label>
                  <input 
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="رمز عبور را مجددا وارد کنید"
                    className={`w-full border py-2 px-4 rounded-lg text-md min-h-[54px] focus:outline-none ${
                      formData.confirmPassword === '' 
                        ? 'border-slate-200 focus:border-[#00bcd4]'
                        : passwordsMatch 
                          ? 'border-green-300 focus:border-green-500' 
                          : 'border-red-300 focus:border-red-500'
                    }`}
                    required
                    minLength={8}
                  />
                  
                  {/* Password confirmation indicator */}
                  {formData.confirmPassword !== '' && (
                    <div className="mt-2">
                      {passwordsMatch ? (
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-xs text-green-600">
                            رمز عبور مطابقت دارد ✓
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span className="text-xs text-red-600">
                            رمز عبور مطابقت ندارد ✗
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {error && (
                <p className="text-red-500 text-sm mt-4">{error}</p>
              )}
              
            </div>
            <div className="fixed right-0 left-0 bottom-0 p-4 border-t-1 border-slate-200 md:relative md:p-0 md:border-0 md:mt-8 md:w-full z-90">
              <button 
                type="submit" 
                disabled={isLoading || !passwordStrength.isStrong || !formData.name.trim() || !formData.lastName.trim() || !passwordsMatch || formData.confirmPassword === ''}
                className={`w-full py-2 px-4 rounded-lg text-md min-h-[48px] ${
                  isLoading || !passwordStrength.isStrong || !formData.name.trim() || !formData.lastName.trim() || !passwordsMatch || formData.confirmPassword === ''
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-[#1a237e] hover:bg-[#00acc1] cursor-pointer'
                } text-white`}
              >
                {isLoading ? 'در حال ثبت...' : 'تکمیل ثبت نام'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CompleteRegistration; 